import { useMutation, useQuery } from "@apollo/client";
import {
  Banner,
  Button,
  ConfirmDialog,
  CopyIconButton,
  Drawer,
  DrawerBody,
  Modal,
  ModalBody,
  Tag,
  Text,
  Toast,
} from "@aurem/ui";
import { FormattedDate } from "components/display/FormattedDate";
import {
  attachmentRunsByRun,
  companyDetails,
  companyProductInstance,
  editEmployee,
  marketOrderDetails,
  paymentDetails,
  workflowRunDetails,
} from "const/routes";
import { useAuthorize } from "graphql/user";
import { useBasePath } from "hooks/useBasePath";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
} from "lucide-react";
import { useI18n } from "providers/I18nProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  gql,
  InternalWorkflowRunDetailQuery,
  InternalWorkflowRunsFilterField,
  InternalWorkflowRunsFiltersExpression,
  Operation,
  Operator,
  PermissionEnum,
  WorkflowConfigFetchStatus,
  WorkflowRunStatus,
  WorkflowRunSubjectType,
} from "types";
import { formatDate } from "utils/dates";
import { getBlockingRunIds } from "./barrier-blocking.utils";
import { canContinueTimerNode } from "./continue-timer.utils";
import { useContinueTimer } from "./hooks/use-continue-timer.hooks";
import { useRetryConfigFetch } from "./hooks/use-retry-config-fetch.hooks";
import { useSignalWorkflowRun } from "./hooks/use-signal-workflow-run.hooks";
import { useWorkflowRunLifecycle } from "./hooks/use-workflow-run-lifecycle.hooks";
import { getResettableEventIds } from "./reset-node.utils";
import { ResetNodeConfirmDialog } from "./ResetNodeConfirmDialog";
import { canSignalWorkflowRun, isRunBlockedOnConfig } from "./signal-run.utils";
import { WorkflowConfigFetchTag } from "./WorkflowConfigFetchTag";

const GET_WORKFLOW_RUN_DETAIL = gql(/* GraphQL */ `
  query InternalWorkflowRunDetail(
    $filters: InternalWorkflowRunsFiltersExpression
  ) {
    internalWorkflowRunList(
      pagination: { limit: 1, offset: 0 }
      filters: $filters
    ) {
      list {
        id
        status
        workflowVersionId
        workflowKey
        workflowConfigPin {
          commitSha
          versionAt
        }
        workflowName
        createdAt
        input
        configFetch {
          status
          error
          commitSha
        }
        subject {
          type
          id
          name
          companyId
        }
        workflowVersion {
          version
          workflowDefinition {
            name
          }
          nodes {
            id
            config
          }
        }
        workflowEvents {
          id
          seq
          type
          nodeId
          patch
          deletedAt
          createdAt
        }
        barrierWaiters {
          nodeId
          blockingRunIds
        }
      }
    }
  }
`);

const RESET_WORKFLOW_FROM_NODE = gql(/* GraphQL */ `
  mutation RerunWorkflowNode($runId: String!, $nodeKey: String!) {
    rerunWorkflowNode(runId: $runId, nodeKey: $nodeKey) {
      id
      status
    }
  }
`);

type WorkflowRunDetailsDrawerProps = {
  selectedId: string;
  onClose: () => void;
};

type TreeState = {
  expandedByDefault: boolean;
  overrides: Set<string>;
  toggle: (path: string) => void;
};

const isObjectLike = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const unescapeString = (value: string): string => {
  if (!value.includes("\\")) return value;
  try {
    return JSON.parse(`"${value.replace(/"/g, '\\"')}"`);
  } catch {
    return value;
  }
};

const formatJsonPrimitive = (value: unknown): string => {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return unescapeString(value);
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return String(value);
};

const getValueTypeLabel = (value: unknown): string => {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (Array.isArray(value)) return "array";
  return typeof value;
};

const JsonTreeNode = ({
  label,
  value,
  depth,
  path,
  state,
}: {
  label: string;
  value: unknown;
  depth: number;
  path: string;
  state: TreeState;
}) => {
  const isExpandablePrimitive = depth === 0 && !isObjectLike(value);
  const isExpandable = isObjectLike(value) || isExpandablePrimitive;

  if (!isExpandable) {
    return (
      <div className="flex items-start gap-2 text-xs font-mono">
        <span className="text-neutral-600">{label}:</span>
        <span className="text-neutral-900 whitespace-pre-wrap break-words">
          {formatJsonPrimitive(value)}
        </span>
      </div>
    );
  }

  const entries = isObjectLike(value)
    ? Array.isArray(value)
      ? value.map((item, index) => [String(index), item] as const)
      : Object.entries(value)
    : [];
  const summarySuffix = isObjectLike(value)
    ? Array.isArray(value)
      ? `[${entries.length}]`
      : `{${entries.length}}`
    : `(${getValueTypeLabel(value)})`;

  const isOpen = state.overrides.has(path)
    ? !state.expandedByDefault
    : state.expandedByDefault;

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => state.toggle(path)}
        className="flex items-center gap-1 text-xs font-mono text-neutral-600"
      >
        {isOpen ? (
          <ChevronDownIcon className="w-3 h-3" />
        ) : (
          <ChevronRightIcon className="w-3 h-3" />
        )}
        <span>{label}</span>
        <span className="text-neutral-400">{summarySuffix}</span>
      </button>
      {isOpen && (
        <div className="ps-4 border-l border-neutral-500/10 flex flex-col gap-1">
          {isObjectLike(value) ? (
            entries.map(([key, val]) => (
              <JsonTreeNode
                key={`${path}.${key}`}
                label={key}
                value={val}
                depth={depth + 1}
                path={`${path}.${key}`}
                state={state}
              />
            ))
          ) : (
            <div className="flex items-start gap-2 text-xs font-mono">
              <span className="text-neutral-900 whitespace-pre-wrap break-words">
                {formatJsonPrimitive(value)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const JsonTree = ({
  value,
  showControls = true,
  surfaceClassName,
}: {
  value: unknown;
  showControls?: boolean;
  surfaceClassName?: string;
}) => {
  const { t } = useI18n();
  const [expandedByDefault, setExpandedByDefault] = useState(true);
  const [overrides, setOverrides] = useState<Set<string>>(new Set());

  const state = useMemo<TreeState>(
    () => ({
      expandedByDefault,
      overrides,
      toggle: (path: string) => {
        setOverrides((prev) => {
          const next = new Set(prev);
          if (next.has(path)) {
            next.delete(path);
          } else {
            next.add(path);
          }
          return next;
        });
      },
    }),
    [expandedByDefault, overrides],
  );

  const handleExpandAll = useCallback(() => {
    setExpandedByDefault(true);
    setOverrides(new Set());
  }, []);

  const handleCollapseAll = useCallback(() => {
    setExpandedByDefault(false);
    setOverrides(new Set());
  }, []);

  if (!isObjectLike(value)) {
    return (
      <pre className="text-xs max-h-[70vh] overflow-auto bg-neutral-500/5 p-4 rounded-md whitespace-pre-wrap break-words font-mono">
        {formatJsonPrimitive(value)}
      </pre>
    );
  }

  const entries = Array.isArray(value)
    ? value.map((item, index) => [String(index), item] as const)
    : Object.entries(value);

  const surfaceClasses = ["flex flex-col gap-1", surfaceClassName]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="flex flex-col gap-2">
      {showControls && (
        <div className="flex items-center gap-2">
          <Button
            size="xs"
            variant="outlined"
            onClick={handleExpandAll}
            aria-label={t(
              "apps.admin.internalResources.workflowRun.detail.expandAll",
            )}
          >
            {t("apps.admin.internalResources.workflowRun.detail.expandAll")}
          </Button>
          <Button
            size="xs"
            variant="outlined"
            onClick={handleCollapseAll}
            aria-label={t(
              "apps.admin.internalResources.workflowRun.detail.collapseAll",
            )}
          >
            {t("apps.admin.internalResources.workflowRun.detail.collapseAll")}
          </Button>
        </div>
      )}
      <div className={surfaceClasses}>
        {entries.map(([key, val]) => (
          <JsonTreeNode
            key={key}
            label={key}
            value={val}
            depth={0}
            path={key}
            state={state}
          />
        ))}
      </div>
    </div>
  );
};

const JsonModal = ({
  title,
  content,
  value,
  open,
  onOpenChange,
  isCode,
}: {
  title: string;
  content: string;
  value: unknown;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isCode?: boolean;
}) => (
  <Modal
    open={open}
    onOpenChange={onOpenChange}
    title={title}
    className="w-full max-w-4xl h-[70vh] max-h-[90vh]"
  >
    <ModalBody className="flex flex-col gap-4 min-h-0">
      {isCode ? (
        <pre className="text-xs max-h-[70vh] overflow-auto bg-neutral-500/5 p-4 rounded-md whitespace-pre-wrap break-words font-mono">
          {content}
        </pre>
      ) : (
        <JsonTree
          value={value}
          surfaceClassName="text-xs bg-neutral-500/5 p-4 rounded-md font-mono"
        />
      )}
    </ModalBody>
  </Modal>
);

const JsonSection = ({
  title,
  value,
  isCode,
}: {
  title: string;
  value: unknown;
  isCode?: boolean;
}) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  if (value === undefined || value === null) return null;

  let content: string;
  try {
    content =
      typeof value === "string" ? value : JSON.stringify(value, null, 2);
  } catch {
    content = String(value);
  }
  const displayContent =
    typeof value === "string" ? formatJsonPrimitive(value) : content;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Text size="sm" weight="medium">
          {title}
        </Text>
        <div className="flex items-center gap-2">
          <CopyIconButton copyContent={content} size="xs" variant="ghost" />
          <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
            {t("apps.admin.internalResources.workflowRun.detail.viewFull")}
          </Button>
        </div>
      </div>
      <pre className="text-xs max-h-64 overflow-auto bg-neutral-500/5 p-3 rounded-md whitespace-pre-wrap break-words font-mono">
        {isCode ? content : displayContent}
      </pre>
      <JsonModal
        title={title}
        content={content}
        value={value}
        open={open}
        onOpenChange={setOpen}
        isCode={isCode}
      />
    </div>
  );
};

type NodeResultLink = { id: string; label: string; path: string };

const toResultLinks = (
  items: unknown,
  toPath: (id: string) => string,
): NodeResultLink[] =>
  (Array.isArray(items) ? items : []).flatMap((item) => {
    if (!isObjectLike(item) || typeof item.id !== "string") return [];
    return [
      {
        id: item.id,
        label: typeof item.reference === "string" ? item.reference : item.id,
        path: toPath(item.id),
      },
    ];
  });

const getNodeResultLinks = (
  nodeId: string | null | undefined,
  result: unknown,
): { links: NodeResultLink[]; nodeType: string } => {
  const nodeType = nodeId?.split(":")[0] ?? "";
  if (!isObjectLike(result)) return { links: [], nodeType };

  if (nodeType === "payment") {
    return {
      nodeType,
      links: toResultLinks(result.payments, (paymentId) =>
        paymentDetails({ paymentId }),
      ),
    };
  }

  if (nodeType === "market-order") {
    return {
      nodeType,
      links: toResultLinks(result.marketOrders, (marketOrderId) =>
        marketOrderDetails({ marketOrderId }),
      ),
    };
  }

  return { links: [], nodeType };
};

const NodeResultLinks = ({
  title,
  links,
}: {
  title: string;
  links: NodeResultLink[];
}) => {
  const { path } = useBasePath();

  if (links.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <Text size="sm" weight="medium">
        {title}
      </Text>
      <div className="flex flex-wrap gap-2">
        {links.map((link) => (
          <Button
            key={link.id}
            as="a"
            href={path(link.path)}
            target="_blank"
            rel="noopener noreferrer"
            size="xs"
            variant="outlined"
            endIcon={<ExternalLinkIcon className="size-3" />}
          >
            {link.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

const AttachmentRunsLink = ({
  title,
  runId,
}: {
  title: string;
  runId: string;
}) => {
  const { path } = useBasePath();

  if (!runId) return null;

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        as="a"
        href={path(attachmentRunsByRun({ runId }))}
        target="_blank"
        rel="noopener noreferrer"
        size="xs"
        variant="outlined"
        endIcon={<ExternalLinkIcon className="size-3" />}
      >
        {title}
      </Button>
    </div>
  );
};

const BlockingRunLinks = ({
  title,
  runIds,
}: {
  title: string;
  runIds: readonly string[];
}) => {
  const { path } = useBasePath();

  if (runIds.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <Text size="sm" weight="medium">
        {title}
      </Text>
      <div className="flex flex-wrap gap-2">
        {runIds.map((runId) => (
          <Button
            key={runId}
            as="a"
            href={path(workflowRunDetails({ runId }))}
            target="_blank"
            rel="noopener noreferrer"
            size="xs"
            variant="outlined"
            endIcon={<ExternalLinkIcon className="size-3" />}
          >
            {runId}
          </Button>
        ))}
      </div>
    </div>
  );
};

type WorkflowRunSubject =
  InternalWorkflowRunDetailQuery["internalWorkflowRunList"]["list"][number]["subject"];

const SCOPE_LABELS: Record<WorkflowRunSubjectType, string> = {
  [WorkflowRunSubjectType.Employee]: "Employee",
  [WorkflowRunSubjectType.Company]: "Company",
  [WorkflowRunSubjectType.ProductInstance]: "Product instance",
};

const getScopeLink = (
  subject: WorkflowRunSubject,
): { label: string; path: string } | null => {
  if (!subject) return null;

  const scopedTo = subject.name ?? subject.id;
  const label = `${SCOPE_LABELS[subject.type]}: ${scopedTo}`;

  switch (subject.type) {
    case WorkflowRunSubjectType.Company:
      return {
        label,
        path: companyDetails({ companyId: subject.id, tab: "overview" }),
      };
    case WorkflowRunSubjectType.Employee:
      if (!subject.companyId) return null;
      return {
        label,
        path: editEmployee({
          companyId: subject.companyId,
          employeeId: subject.id,
        }),
      };
    case WorkflowRunSubjectType.ProductInstance:
      if (!subject.companyId) return null;
      return {
        label,
        path: companyProductInstance({
          companyId: subject.companyId,
          productInstanceId: subject.id,
        }),
      };
    default:
      return null;
  }
};

const ScopeLink = ({ subject }: { subject: WorkflowRunSubject }) => {
  const { path } = useBasePath();
  const link = getScopeLink(subject);

  if (!link) return null;

  return (
    <div className="flex">
      <Button
        as="a"
        href={path(link.path)}
        target="_blank"
        rel="noopener noreferrer"
        size="xs"
        variant="outlined"
        endIcon={<ExternalLinkIcon className="size-3" />}
      >
        {link.label}
      </Button>
    </div>
  );
};

const MetaSections = ({
  meta,
  labels,
}: {
  meta: Record<string, unknown> | undefined;
  labels: { meta: string; code: string };
}) => {
  if (!meta) return null;

  const { code, ...rest } = meta;

  return (
    <div className="flex flex-col gap-4">
      {typeof code === "string" && (
        <JsonSection title={labels.code} value={code} isCode />
      )}
      {Object.keys(rest).length > 0 && (
        <JsonSection title={labels.meta} value={rest} />
      )}
    </div>
  );
};

const EventTimelineItem = ({
  event,
  inputConfig,
  blockingRunIds,
  labels,
  onResetNode,
  canResetFromEvent,
  resetting,
  onContinueTimer,
  continuingTimer,
  runId,
}: {
  event: InternalWorkflowRunDetailQuery["internalWorkflowRunList"]["list"][number]["workflowEvents"][number];
  inputConfig: unknown;
  blockingRunIds: readonly string[];
  labels: {
    started: string;
    pending: string;
    completed: string;
    completedWithErrors: string;
    failed: string;
    skipped: string;
    exited: string;
    input: string;
    output: string;
    error: string;
    meta: string;
    code: string;
    generatedPayments: string;
    generatedMarketOrders: string;
    blockedBy: string;
    viewAttachmentRuns: string;
  };
  onResetNode?: (nodeId: string) => Promise<void>;
  canResetFromEvent?: boolean;
  resetting?: boolean;
  onContinueTimer?: (nodeId: string) => Promise<void>;
  continuingTimer?: boolean;
  runId: string;
}) => {
  const { t } = useI18n();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [continueConfirmOpen, setContinueConfirmOpen] = useState(false);
  const eventType = event.type.split(":")[1] || event.type;
  const canReset = !!canResetFromEvent && !!event.nodeId && !!onResetNode;
  const canContinueTimer = canContinueTimerNode(event, !!onContinueTimer);

  const handleConfirmReset = useCallback(() => {
    if (!event.nodeId || !onResetNode) return;
    return onResetNode(event.nodeId);
  }, [event.nodeId, onResetNode]);

  const handleConfirmContinueTimer = useCallback(() => {
    if (!event.nodeId || !onContinueTimer) return;
    return onContinueTimer(event.nodeId);
  }, [event.nodeId, onContinueTimer]);
  const statusLabel =
    eventType === "started"
      ? labels.started
      : eventType === "pending"
        ? labels.pending
        : eventType === "completed"
          ? labels.completed
          : eventType === "completed_with_errors"
            ? labels.completedWithErrors
            : eventType === "failed"
              ? labels.failed
              : eventType === "exited"
                ? labels.exited
                : labels.skipped;

  const statusColor =
    eventType === "completed"
      ? "success"
      : eventType === "completed_with_errors"
        ? "warning"
        : eventType === "failed"
          ? "danger"
          : eventType === "pending"
            ? "warning"
            : "neutral";

  const patch = event.patch ?? undefined;
  const { links: resultLinks, nodeType } = getNodeResultLinks(
    event.nodeId,
    patch?.result,
  );

  return (
    <div className={`relative pb-6${event.deletedAt ? " opacity-50" : ""}`}>
      <div className="absolute start-[3px] top-3 h-2 w-2 rounded-full bg-neutral-400" />
      <div className="ps-6">
        <div className="flex items-center gap-2 mb-2">
          <Text size="sm" weight="medium">
            {event.nodeId || "-"}
          </Text>
          <Tag color={statusColor} label={statusLabel} />
          <Text size="xs" className="text-neutral-400">
            <FormattedDate value={event.createdAt} withSeconds />
          </Text>
          {(canContinueTimer || canReset) && (
            <div className="ms-auto flex items-center gap-2">
              {canContinueTimer && (
                <Button
                  size="xs"
                  variant="outlined"
                  loading={continuingTimer}
                  onClick={() => setContinueConfirmOpen(true)}
                >
                  {t(
                    "apps.admin.internalResources.workflowRun.detail.continueTimer",
                  )}
                </Button>
              )}
              {canReset && (
                <Button
                  size="xs"
                  variant="outlined"
                  color="destructive"
                  loading={resetting}
                  onClick={() => setConfirmOpen(true)}
                >
                  {t(
                    "apps.admin.internalResources.workflowRun.detail.resetNode",
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
        {canContinueTimer && (
          <ConfirmDialog
            open={continueConfirmOpen}
            onOpenChange={setContinueConfirmOpen}
            title={t(
              "apps.admin.internalResources.workflowRun.detail.continueTimerConfirmTitle",
            )}
            description={t(
              "apps.admin.internalResources.workflowRun.detail.continueTimerConfirmDescription",
              event.nodeId ?? "",
            )}
            submitText={t(
              "apps.admin.internalResources.workflowRun.detail.continueTimer",
            )}
            submitAction={handleConfirmContinueTimer}
          />
        )}
        {canReset && (
          <ResetNodeConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            nodeId={event.nodeId ?? ""}
            nodeType={nodeType}
            sideEffectCount={resultLinks.length}
            onConfirm={handleConfirmReset}
          />
        )}
        <div className="flex flex-col gap-4">
          <BlockingRunLinks title={labels.blockedBy} runIds={blockingRunIds} />
          {eventType !== "started" && nodeType === "attachment" && (
            <AttachmentRunsLink
              title={labels.viewAttachmentRuns}
              runId={runId}
            />
          )}
          {eventType === "started" && (
            <JsonSection title={labels.input} value={inputConfig} />
          )}
          {eventType !== "started" && (
            <NodeResultLinks
              title={
                nodeType === "payment"
                  ? labels.generatedPayments
                  : labels.generatedMarketOrders
              }
              links={resultLinks}
            />
          )}
          {eventType !== "started" && patch?.result !== undefined && (
            <JsonSection title={labels.output} value={patch.result} />
          )}
          {eventType !== "started" && patch?.error && (
            <JsonSection title={labels.error} value={patch.error} />
          )}
          {eventType !== "started" && patch?.meta && (
            <MetaSections
              meta={patch.meta}
              labels={{ meta: labels.meta, code: labels.code }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Recovery actions for a run stranded in `Created` — the DAG runner never
 * produced an event, so the run is neither progressing nor closed.
 */
const StuckRunActions = ({
  onContinue,
  onExit,
  continuing,
  exiting,
}: {
  onContinue: () => Promise<void>;
  onExit: () => Promise<void>;
  continuing: boolean;
  exiting: boolean;
}) => {
  const { t } = useI18n();
  const [continueConfirmOpen, setContinueConfirmOpen] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outlined"
        loading={continuing}
        onClick={() => setContinueConfirmOpen(true)}
      >
        {t("apps.admin.internalResources.workflowRun.detail.continueRun")}
      </Button>
      <Button
        size="sm"
        variant="outlined"
        loading={exiting}
        onClick={() => setExitConfirmOpen(true)}
      >
        {t("apps.admin.internalResources.workflowRun.detail.exitRun")}
      </Button>
      <ConfirmDialog
        open={continueConfirmOpen}
        onOpenChange={setContinueConfirmOpen}
        title={t(
          "apps.admin.internalResources.workflowRun.detail.continueRunConfirmTitle",
        )}
        description={t(
          "apps.admin.internalResources.workflowRun.detail.continueRunConfirmDescription",
        )}
        submitText={t(
          "apps.admin.internalResources.workflowRun.detail.continueRun",
        )}
        submitAction={onContinue}
      />
      <ConfirmDialog
        open={exitConfirmOpen}
        onOpenChange={setExitConfirmOpen}
        title={t(
          "apps.admin.internalResources.workflowRun.detail.exitRunConfirmTitle",
        )}
        description={t(
          "apps.admin.internalResources.workflowRun.detail.exitRunConfirmDescription",
        )}
        submitText={t(
          "apps.admin.internalResources.workflowRun.detail.exitRun",
        )}
        submitAction={onExit}
        variant="danger"
      />
    </div>
  );
};

const SignalRunAction = ({
  onSignal,
  signalling,
}: {
  onSignal: () => Promise<void>;
  signalling: boolean;
}) => {
  const { t } = useI18n();
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outlined"
        loading={signalling}
        onClick={() => setConfirmOpen(true)}
      >
        {t("apps.admin.internalResources.workflowRun.detail.signalRun")}
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t(
          "apps.admin.internalResources.workflowRun.detail.signalRunConfirmTitle",
        )}
        description={t(
          "apps.admin.internalResources.workflowRun.detail.signalRunConfirmDescription",
        )}
        submitText={t(
          "apps.admin.internalResources.workflowRun.detail.signalRun",
        )}
        submitAction={onSignal}
      />
    </div>
  );
};

export const WorkflowRunDetailsDrawer = ({
  selectedId,
  onClose,
}: WorkflowRunDetailsDrawerProps) => {
  const { t } = useI18n();

  const detailFilters = useMemo<
    InternalWorkflowRunsFiltersExpression | undefined
  >(() => {
    if (!selectedId) return undefined;

    return {
      operator: Operator.And,
      filters: [
        {
          op: Operation.Eq,
          field: InternalWorkflowRunsFilterField.Id,
          values: [selectedId],
        },
      ],
      childExpressions: [],
    };
  }, [selectedId]);

  const {
    data: eventsData,
    loading: eventsLoading,
    refetch: refetchEvents,
  } = useQuery(GET_WORKFLOW_RUN_DETAIL, {
    variables: { filters: detailFilters },
    skip: !selectedId,
  });

  const canUseSupportTools = useAuthorize(PermissionEnum.ManageSupportTools);

  const [resetWorkflowFromNode, { loading: resetting }] = useMutation(
    RESET_WORKFLOW_FROM_NODE,
  );

  const { continueTimer, loading: continuingTimer } = useContinueTimer();

  const { retryConfigFetch, loading: retryingConfigFetch } =
    useRetryConfigFetch();

  const {
    continueRun,
    exitRun,
    continuing: continuingRun,
    exiting: exitingRun,
  } = useWorkflowRunLifecycle();

  const { signalRun, loading: signallingRun } = useSignalWorkflowRun();

  const handleResetNode = useCallback(
    async (nodeId: string) => {
      if (!selectedId) return;
      try {
        await resetWorkflowFromNode({
          variables: { runId: selectedId, nodeKey: nodeId },
        });
        await refetchEvents();
        Toast.success(
          {
            title: t(
              "apps.admin.internalResources.workflowRun.detail.resetNodeSuccess",
            ),
          },
          { position: "bottom-right" },
        );
      } catch (error) {
        Toast.error(
          {
            title: t(
              "apps.admin.internalResources.workflowRun.detail.resetNodeError",
            ),
            text: error instanceof Error ? error.message : "Unknown error",
          },
          { position: "bottom-right" },
        );
      }
    },
    [resetWorkflowFromNode, refetchEvents, selectedId, t],
  );

  const handleContinueTimer = useCallback(
    async (nodeId: string) => {
      if (!selectedId) return;
      try {
        await continueTimer(selectedId, nodeId);
        await refetchEvents();
        Toast.success(
          {
            title: t(
              "apps.admin.internalResources.workflowRun.detail.continueTimerSuccess",
            ),
          },
          { position: "bottom-right" },
        );
      } catch (error) {
        Toast.error(
          {
            title: t(
              "apps.admin.internalResources.workflowRun.detail.continueTimerError",
            ),
            text: error instanceof Error ? error.message : "Unknown error",
          },
          { position: "bottom-right" },
        );
      }
    },
    [continueTimer, refetchEvents, selectedId, t],
  );

  const handleRetryConfigFetch = useCallback(async () => {
    if (!selectedId) return;
    try {
      const outcome = await retryConfigFetch(selectedId);
      await refetchEvents();

      if (outcome?.status !== WorkflowConfigFetchStatus.Ok) {
        Toast.error(
          {
            title: t(
              "apps.admin.internalResources.workflowRun.configFetch.retryFailed",
            ),
            text: outcome?.error ?? undefined,
          },
          { position: "bottom-right" },
        );
        return;
      }

      Toast.success(
        {
          title: t(
            "apps.admin.internalResources.workflowRun.configFetch.retrySuccess",
          ),
        },
        { position: "bottom-right" },
      );
    } catch (error) {
      Toast.error(
        {
          title: t(
            "apps.admin.internalResources.workflowRun.configFetch.retryError",
          ),
          text: error instanceof Error ? error.message : "Unknown error",
        },
        { position: "bottom-right" },
      );
    }
  }, [retryConfigFetch, refetchEvents, selectedId, t]);

  const handleContinueRun = useCallback(async () => {
    if (!selectedId) return;
    try {
      await continueRun(selectedId);
      await refetchEvents();
      Toast.success(
        {
          title: t(
            "apps.admin.internalResources.workflowRun.detail.continueRunSuccess",
          ),
        },
        { position: "bottom-right" },
      );
    } catch (error) {
      Toast.error(
        {
          title: t(
            "apps.admin.internalResources.workflowRun.detail.continueRunError",
          ),
          text: error instanceof Error ? error.message : "Unknown error",
        },
        { position: "bottom-right" },
      );
    }
  }, [continueRun, refetchEvents, selectedId, t]);

  const handleExitRun = useCallback(async () => {
    if (!selectedId) return;
    try {
      await exitRun(selectedId);
      await refetchEvents();
      Toast.success(
        {
          title: t(
            "apps.admin.internalResources.workflowRun.detail.exitRunSuccess",
          ),
        },
        { position: "bottom-right" },
      );
    } catch (error) {
      Toast.error(
        {
          title: t(
            "apps.admin.internalResources.workflowRun.detail.exitRunError",
          ),
          text: error instanceof Error ? error.message : "Unknown error",
        },
        { position: "bottom-right" },
      );
    }
  }, [exitRun, refetchEvents, selectedId, t]);

  const handleSignalRun = useCallback(async () => {
    if (!selectedId) return;
    try {
      await signalRun(selectedId);
      await refetchEvents();
      Toast.success(
        {
          title: t(
            "apps.admin.internalResources.workflowRun.detail.signalRunSuccess",
          ),
        },
        { position: "bottom-right" },
      );
    } catch (error) {
      Toast.error(
        {
          title: t(
            "apps.admin.internalResources.workflowRun.detail.signalRunError",
          ),
          text: error instanceof Error ? error.message : "Unknown error",
        },
        { position: "bottom-right" },
      );
    }
  }, [signalRun, refetchEvents, selectedId, t]);

  const selectedRun = useMemo(() => {
    return eventsData?.internalWorkflowRunList?.list?.[0] ?? null;
  }, [eventsData]);

  const events = useMemo(() => {
    const list = selectedRun?.workflowEvents ?? [];
    return [...list].sort((a, b) => b.seq - a.seq);
  }, [selectedRun]);

  const nodeConfigs = useMemo(() => {
    const nodes = selectedRun?.workflowVersion?.nodes ?? [];
    return new Map(nodes.map((node) => [node.id, node.config]));
  }, [selectedRun]);

  const barrierWaiters = useMemo(
    () => selectedRun?.barrierWaiters ?? [],
    [selectedRun],
  );

  const timelineEvents = useMemo(() => {
    const skippedNodeIds = new Set(
      events
        .filter((event) => {
          if (!event.type.startsWith("node:")) return false;
          const eventType = event.type.split(":")[1] || event.type;
          return eventType === "skipped";
        })
        .map((event) => event.nodeId)
        .filter((nodeId): nodeId is string => Boolean(nodeId)),
    );

    return events.filter((event) => {
      if (event.type === "workflow:exited") return !!event.patch;
      if (!event.type.startsWith("node:")) return false;
      const eventType = event.type.split(":")[1] || event.type;
      if (eventType === "skipped") return false;
      if (event.nodeId && skippedNodeIds.has(event.nodeId)) return false;
      return true;
    });
  }, [events]);

  const resettableEventIds = useMemo(
    () => getResettableEventIds(timelineEvents, canUseSupportTools),
    [timelineEvents, canUseSupportTools],
  );

  useEffect(() => {
    if (!selectedId) return;
    if (
      selectedRun?.status !== WorkflowRunStatus.Running &&
      selectedRun?.status !== WorkflowRunStatus.Blocked
    ) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void refetchEvents();
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [refetchEvents, selectedRun?.status, selectedId]);

  const timelineLabels = useMemo(
    () => ({
      started: t("apps.admin.internalResources.workflowRun.events.started"),
      pending: t("apps.admin.internalResources.workflowRun.events.pending"),
      completed: t("apps.admin.internalResources.workflowRun.events.completed"),
      completedWithErrors: t(
        "apps.admin.internalResources.workflowRun.events.completedWithErrors",
      ),
      failed: t("apps.admin.internalResources.workflowRun.events.failed"),
      skipped: t("apps.admin.internalResources.workflowRun.events.skipped"),
      exited: t("apps.admin.internalResources.workflowRun.events.exited"),
      input: t("apps.admin.internalResources.workflowRun.detail.input"),
      output: t("apps.admin.internalResources.workflowRun.detail.output"),
      error: t("apps.admin.internalResources.workflowRun.detail.error"),
      meta: t("apps.admin.internalResources.workflowRun.detail.meta"),
      code: t("apps.admin.internalResources.workflowRun.detail.code"),
      generatedPayments: t(
        "apps.admin.internalResources.workflowRun.detail.generatedPayments",
      ),
      generatedMarketOrders: t(
        "apps.admin.internalResources.workflowRun.detail.generatedMarketOrders",
      ),
      blockedBy: t("apps.admin.internalResources.workflowRun.detail.blockedBy"),
      viewAttachmentRuns: t(
        "apps.admin.internalResources.workflowRun.detail.viewAttachmentRuns",
      ),
    }),
    [t],
  );

  // Config-driven runs carry their name and config version on the run itself;
  // the rest name their workflow through the version they were built from.
  const drawerTitle = useMemo(() => {
    if (!selectedRun) return "";

    const name =
      selectedRun.workflowName ??
      selectedRun.workflowVersion?.workflowDefinition?.name;
    if (!name) {
      return t("apps.admin.internalResources.workflowRun.detail.title");
    }

    const pin = selectedRun.workflowConfigPin;
    const version = selectedRun.workflowVersion?.version;
    const label = pin?.versionAt
      ? formatDate(pin.versionAt, "dd MMM yyyy")
      : version
        ? `v${version}`
        : undefined;

    return t(
      "apps.admin.internalResources.workflowRun.detail.titleWithWorkflow",
      label ? `${name} - ${label}` : name,
    );
  }, [selectedRun, t]);

  return (
    <Drawer
      open={!!selectedId}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={drawerTitle}
      className="w-[720px] max-w-[95vw]"
    >
      <DrawerBody className="flex flex-col gap-6">
        {selectedRun && (
          <div className="flex flex-col gap-2">
            {selectedRun.workflowKey && (
              <div className="flex">
                <WorkflowConfigFetchTag
                  status={selectedRun.configFetch?.status}
                  error={selectedRun.configFetch?.error}
                  commitSha={selectedRun.configFetch?.commitSha}
                  onRetry={
                    canUseSupportTools ? handleRetryConfigFetch : undefined
                  }
                  retrying={retryingConfigFetch}
                />
              </div>
            )}
            <JsonSection
              title={t("apps.admin.internalResources.workflowRun.detail.run")}
              value={selectedRun.input}
            />
            <ScopeLink subject={selectedRun.subject} />
          </div>
        )}

        {selectedRun && isRunBlockedOnConfig(selectedRun) && (
          <Banner
            variant="inline"
            color="warning"
            role="status"
            text={t(
              "apps.admin.internalResources.workflowRun.detail.configBlocked",
            )}
          />
        )}

        <div className="flex items-center gap-4">
          <Text size="sm" weight="medium">
            {t("apps.admin.internalResources.workflowRun.detail.events")}
          </Text>
          {canUseSupportTools &&
            selectedRun?.status === WorkflowRunStatus.Created && (
              <div className="ms-auto">
                <StuckRunActions
                  onContinue={handleContinueRun}
                  onExit={handleExitRun}
                  continuing={continuingRun}
                  exiting={exitingRun}
                />
              </div>
            )}
          {selectedRun &&
            canSignalWorkflowRun(selectedRun, canUseSupportTools) && (
              <div className="ms-auto">
                <SignalRunAction
                  onSignal={handleSignalRun}
                  signalling={signallingRun}
                />
              </div>
            )}
        </div>

        {eventsLoading && (
          <Text size="sm" className="text-neutral-400">
            {t("apps.admin.common.loading")}
          </Text>
        )}

        {!eventsLoading && timelineEvents.length === 0 && (
          <Text size="sm" className="text-neutral-400">
            {t("apps.admin.internalResources.workflowRun.detail.noEvents")}
          </Text>
        )}

        {!eventsLoading && timelineEvents.length > 0 && (
          <div className="relative">
            <div className="absolute start-[6px] top-2 h-full w-px bg-neutral-500/10" />
            <div className="flex flex-col">
              {timelineEvents.map((event) => (
                <EventTimelineItem
                  key={event.id}
                  event={event}
                  inputConfig={
                    event.nodeId ? nodeConfigs.get(event.nodeId) : undefined
                  }
                  blockingRunIds={getBlockingRunIds(event, barrierWaiters)}
                  labels={timelineLabels}
                  onResetNode={canUseSupportTools ? handleResetNode : undefined}
                  canResetFromEvent={resettableEventIds.has(event.id)}
                  resetting={resetting}
                  onContinueTimer={
                    canUseSupportTools ? handleContinueTimer : undefined
                  }
                  continuingTimer={continuingTimer}
                  runId={selectedId}
                />
              ))}
            </div>
          </div>
        )}
      </DrawerBody>
    </Drawer>
  );
};
